<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import CanvasDialogModal from '@/components/dialogs/CanvasDialogModal.vue';
import DataTableStatusBadge from '@/components/tables/DataTableStatusBadge.vue';
import { useUnitBedroomDisplay } from '@/composables/useUnitBedroomDisplay';
import { formatCurrencyWithoutDecimal } from '@/helpers/currencyHelper';
import type { AvailableLocales, LocaleSchema } from '@/locales/index';
import type { Unit } from '@/typings/units/Unit';
import { getCanvasDialogImageUrl } from '@/utils/canvas';
import { formatLongDate } from '@/utils/date';

import { getUnitPositionText } from '@/enums/UnitPosition';
import { UnitStatusEnum } from '../../enums/UnitStatus';
import type { Building } from '@/typings/buildings/Building';
import { ListingTypeEnum } from '@/enums/ListingType';

const { t, locale } = useI18n<{ message: LocaleSchema }, AvailableLocales>({
  useScope: 'global',
});
const { getBedroomCount } = useUnitBedroomDisplay();

const props = defineProps<{
  building?: Building;
  unit?: Unit;
}>();

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
  'unit-selected': [unit: Unit];
  'reset-unit-modal': [];
}>();

const dialogElement = useTemplateRef<InstanceType<typeof CanvasDialogModal>>('dialogElement');

const imageIsLoading = ref(true);

const unitPositionText = computed(() => props.unit ? getUnitPositionText(props.unit.position, t) : null);

const unitAvailable = computed(() => {
  // On permet d'aller dans une fiche d'unité avec ces statuts
  return props.unit?.status == UnitStatusEnum.Available 
    || props.unit?.status == UnitStatusEnum.Rented
    || props.unit?.status == UnitStatusEnum.Reserved
    || props.unit?.status == UnitStatusEnum.Sold;
});

const floorPlanImage = computed(() => {
  // Utiliser la première image de configuration disponible
  if (props.unit?.unitUnitConfigurations && props.unit.unitUnitConfigurations.length > 0) {
    return props.unit.unitUnitConfigurations[0].planImageUrl;
  }
  return null;
});

function selectUnit() {
  if (props.unit) {
    emit('unit-selected', props.unit);
    open.value = false;
  }
}

function closeDialog() {
  open.value = false;
}

function resetDialogState() {
  emit('reset-unit-modal');
  imageIsLoading.value = true;
}
</script>

<template>
  <CanvasDialogModal ref="dialogElement"
                     v-model:open="open"
                     class="canvasDialogModal_unit"
                     :data-unit-status="unit?.status"
                     @dialog-close-transition-ended="resetDialogState">
    <template #image>
      <img v-if="floorPlanImage"
           class="canvasDialogModal-foregroundImage"
           :src="getCanvasDialogImageUrl(floorPlanImage, 700)"
           :data-image-is-loading="imageIsLoading"
           alt=""
           @load="imageIsLoading = false">
      <span v-else>{{ t('common.noImageAvailable') }}</span>
    </template>

    <template #description>
      <hgroup>
        <h3 class="canvasDialogModal-heading">
          {{ `${t('browsingApp.units.unitNumber')} ${unit?.unitNumber}` }}
        </h3>
        <DataTableStatusBadge :status="(unit?.status as string)"
                              :label="t(`browsingApp.statuses.unitStatus.${unit?.status}`)" />
      </hgroup>
      <dl class="canvasDialogModal-descriptionList preferredColumnCountGrid">
        <div v-if="unit?.localizedFloorName" class="canvasDialogModal-descriptionItem">
          <dt>{{ t('browsingApp.units.floor') }}</dt>
          <dd>{{ unit?.localizedFloorName }}</dd>
        </div>
        <div v-if="unit?.livingArea" class="canvasDialogModal-descriptionItem">
          <dt>{{ t('browsingApp.units.livingArea') }}</dt>
          <dd>{{ unit?.livingArea }} {{ t('browsingApp.units.squareFeet') }}</dd>
        </div>
        <div v-if="((building?.listingType === ListingTypeEnum.Sale && unit?.hasDefaultOptions) || building?.listingType === ListingTypeEnum.Rental) && unit?.unitStyleName != null" class="canvasDialogModal-descriptionItem">
          <dt>{{ t('browsingApp.units.style') }}</dt>
          <dd>{{ unit?.unitStyleName }}</dd>
        </div>
        <div v-if="unit?.availabilityDate != null" class="canvasDialogModal-descriptionItem">
          <dt>{{ building?.listingType === ListingTypeEnum.Rental ? t('browsingApp.units.movingDate') : t('browsingApp.units.closestAvailabilityDate')}}</dt>
          <dd>{{ unit.status == UnitStatusEnum.Rented ? t('common.notAvailableAbbreviated') : formatLongDate(unit.availabilityDate.toString(), locale) }}</dd>
        </div>
        <div v-if="unit && building" class="canvasDialogModal-descriptionItem">
          <dt v-if="building?.listingType === ListingTypeEnum.Rental">{{ t('browsingApp.units.pricePerMonth') }}</dt>
          <dt v-else-if="unit?.hasDefaultOptions">{{ t('browsingApp.units.price') }}</dt>
          <dt v-else>{{ t('browsingApp.units.priceFrom') }}</dt>
          <dd>{{ unit.basePrice !== null ? formatCurrencyWithoutDecimal(unit.basePrice, locale) : t('common.notAvailableAbbreviated') }}</dd>
        </div>
        <div v-if="unitPositionText" class="canvasDialogModal-descriptionItem">
          <dt>{{ t('browsingApp.units.position') }}</dt>
          <dd>{{ unitPositionText }}</dd>
        </div>
      </dl>
    </template>

    <template #actions>
      <button v-if="unitAvailable"
              class="btn btn_textWhite canvasDialogModal-button"
              @click="selectUnit">
        {{ t('browsingApp.actions.chooseThisUnit') }}
      </button>
      <button v-else
              class="btn btn_secondary canvasDialogModal-closeButton"
              @click="closeDialog">
        {{ t('common.close') }}
      </button>
    </template>
  </CanvasDialogModal>
</template>


